<template>

  <m-pay-layout>

    <v-layout class="mx-2">
      <v-flex grow>
        <v-divider class="mb-2"></v-divider>
        <MPayAnnouncements
          v-if="announcementDialogShow"
          :announcements="announcements"
          @change="$store.dispatch('announcementSeen'); announcementDialogShow = false"
        />
        <v-divider class="mb-2"></v-divider>
        <v-layout row wrap align-content-space-between align-baseline>

          <v-flex>
            <v-menu
              ref="fromDateMenu"
              :close-on-content-click="false"
              v-model="fromDateMenu"
              :nudge-right="40"
              :return-value.sync="fromDate"
              lazy
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <v-text-field
                slot="activator"
                v-model="fromDate"
                label="From"
                prepend-icon="event"
                readonly
              ></v-text-field>
              <v-date-picker
                v-model="fromDate"
                @input="$refs.fromDateMenu.save(fromDate)"
                :max="toDate"
              ></v-date-picker>
            </v-menu>
          </v-flex>

          <v-flex>
            <v-menu
              ref="toDateMenu"
              :close-on-content-click="false"
              v-model="toDateMenu"
              :nudge-right="40"
              :return-value.sync="toDate"
              lazy
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <v-text-field
                slot="activator"
                v-model="toDate"
                label="To"
                prepend-icon="event"
                readonly
              ></v-text-field>
              <v-date-picker
                v-model="toDate"
                @input="$refs.toDateMenu.save(toDate)"
                :min="fromDate"
              ></v-date-picker>
            </v-menu>
          </v-flex>

          <v-flex>
            <v-btn class="mb-3"
                   :loading="loading"
                   :disabled="loading"
                   :small="true"
                   color="secondary"
                   @click.native="resetFilterLastMonth"
            >
              Last Month
              <v-icon right dark>event</v-icon>
            </v-btn>
          </v-flex>

          <v-flex>
            <v-btn class="mb-3"
                   :loading="loading"
                   :disabled="loading"
                   :small="true"
                   color="secondary"
                   @click.native="resetFilterLastWeek"
            >
              Last Week
              <v-icon right dark>event</v-icon>
            </v-btn>
          </v-flex>

        </v-layout>

        <v-divider class="mb-5"></v-divider>

        <v-layout v-bind="chartBinding">
          <v-flex class="mr-3 mb-5 mt-5">
            <chartist class="pb-5"
                      ratio="ct-golden-section"
                      type="Line"
                      :data="chart1Data"
                      :options="chart1Options"
                      :responsiveOptions="responsiveOptions"
            />
          </v-flex>
          <v-flex class="mr-3 mb-5 mt-5">
            <chartist class="pb-5"
                      ratio="ct-golden-section"
                      type="Line"
                      :data="chart2Data"
                      :options="chart2Options"
                      :responsiveOptions="responsiveOptions"
            />
          </v-flex>
        </v-layout>

      </v-flex>
    </v-layout>
  </m-pay-layout>

</template>

<script>

import moment from 'moment';
import axios from 'axios';

import queries from '../utils/queries';
import chartistOptions from '../chartist/chartist-options';
import MPayAnnouncements from './MPayAnnouncements';
import roles from '../utils/user-roles';

export default {
  name: 'MPayBusinessDays',
  components: {
    MPayAnnouncements,
  },
  data: () => ({
    filtering: {},
    fromDate: moment()
      .subtract(1, 'month')
      .format('YYYY-MM-DD'),
    fromDateMenu: false,
    toDate: moment()
      .format('YYYY-MM-DD'),
    toDateMenu: false,
    loading: false,
    announcementDialogShow: false,
    announcements: null,
    shutdownAnnouncement: [{ title: 'Shutdown alert', text: 'Oversee your company’s transaction flow from a new simple platform, try out the Business Portal. Merchant console will be available till 31.12.2021.' }],
    businessDays: [],
    chart1Options: chartistOptions('Transaction Count', 'Count'),
    chart2Options: chartistOptions('Transaction Amounts', '€'),
    responsiveOptions: [
      ['screen and (min-width: 641px) and (max-width: 1024px)', {
        seriesBarDistance: 10,
        axisX: {
          labelInterpolationFnc(value) {
            return value;
          },
        },
      }],
      ['screen and (max-width: 640px)', {
        seriesBarDistance: 5,
        axisX: {
          labelInterpolationFnc(value) {
            return value;
          },
        },
      }],
    ],
  }),

  computed: {
    chartBinding() {
      const binding = {};
      if (this.$vuetify.breakpoint.smAndDown) {
        binding.column = true;
      }
      return binding;
    },
    chart1Data() {
      const labels = this.filterLabels(this.businessDays.map(bd => bd.date));
      const creditTransactions = this.businessDays.map(bd => bd.creditTransactions);
      const creditReversals = this.businessDays.map(bd => bd.creditReversals);
      const debitTransactions = this.businessDays.map(bd => bd.debitTransactions);
      const debitReversals = this.businessDays.map(bd => bd.debitReversals);
      return {
        labels,
        series: [
          { name: 'Credit Transactions', data: creditTransactions },
          { name: 'Credit Reversals', data: creditReversals },
          { name: 'Debit Transactions', data: debitTransactions },
          { name: 'Debit Reversals', data: debitReversals },
        ],
      };
    },
    chart2Data() {
      const labels = this.filterLabels(this.businessDays.map(bd => bd.date));
      const creditTransactionsAmount = this.businessDays.map(bd => bd.creditTransactionsAmount);
      const creditReversalsAmount = this.businessDays.map(bd => bd.creditReversalsAmount);
      const debitTransactionsAmount = this.businessDays.map(bd => bd.debitTransactionsAmount);
      const debitReversalsAmount = this.businessDays.map(bd => bd.debitReversalsAmount);
      return {
        labels,
        series: [
          { name: 'Credit Transactions', data: creditTransactionsAmount },
          { name: 'Credit Reversals', data: creditReversalsAmount },
          { name: 'Debit Transactions', data: debitTransactionsAmount },
          { name: 'Debit Reversals', data: debitReversalsAmount },
        ],
      };
    },
  },

  methods: {
    resetFilterLastMonth() {
      this.fromDate = moment()
        .subtract(1, 'month')
        .format('YYYY-MM-DD');
      this.toDate = moment()
        .format('YYYY-MM-DD');
    },
    resetFilterLastWeek() {
      this.fromDate = moment()
        .subtract(1, 'week')
        .format('YYYY-MM-DD');
      this.toDate = moment()
        .format('YYYY-MM-DD');
    },
    getBusinessDays() {
      this.businessDays = [];
      this.loading = true;

      axios.get(queries.businessDays(this.fromDate, this.toDate, this.filtering))
        .then((response) => {
          this.businessDays = response.data;
          this.loading = false;
        })
        .catch((e) => {
          this.showError(e);
          this.loading = false;
        });
    },
    getAnnouncements() {
      this.loading = true;
      axios.get(queries.announcements(), {
        data: {},
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then((response) => {
          this.announcements = response.data;
          this.loading = false;
          if (typeof this.announcements !== 'undefined' && this.announcements.length > 0) {
            this.announcementDialogShow = true;
          }
        })
        .catch((e) => {
          this.showError(e);
          this.loading = false;
        });
    },
    filterLabels(labels) {
      return labels.map((label, i, array) => {
        const labelsToShow = 4;
        const itemsCount = array.length;
        const rangeSize = Math.floor(itemsCount / (labelsToShow - 1));

        if (i === itemsCount - 1) {
          return label;
        }
        if (i > itemsCount - rangeSize) {
          return '';
        }
        if (i % rangeSize === 0) {
          return label;
        }
        return '';
      });
    },
    isMerchantConsole() {
      return this.$store.getters.hasAnyRole(roles.UPDATE_MY_MERCHANT);
    },
  },

  watch: {
    fromDate() {
      this.getBusinessDays();
    },
    toDate() {
      this.getBusinessDays();
    },
    $route() {
      this.filtering = queries.getFiltering(this);
      this.getBusinessDays();
    },
  },

  mounted() {
    this.filtering = queries.getFiltering(this);
    this.getBusinessDays();
    if (this.isMerchantConsole() && !this.$store.getters.announcementSeen) {
      this.getAnnouncements();
    }
  },

};
</script>

<style>
  .chartist__no-data {
    color: red;
    border: solid 1px lightgrey;
  }

  .chartist__no-data svg {
    display: none;
  }

  .ct-label.ct-horizontal.ct-end {
    position: absolute;
    left: -30px;
    display: block;
    text-align: right;
  }

  .ct-legend {
    position: absolute;
    top: -50px;
    z-index: 1;
    list-style: none;
    text-align: center;
  }

  .ct-legend li {
    position: relative;
    padding-left: 23px;
    margin-right: 10px;
    margin-bottom: 3px;
    cursor: pointer;
    display: inline-block;
  }

  .ct-legend li:before {
    width: 12px;
    height: 12px;
    position: absolute;
    left: 5px;
    top: 4px;
    content: '';
    border: 3px solid transparent;
    border-radius: 2px;
  }

  .ct-legend li .inactive:before {
    background: transparent;
  }

  .ct-legend li:nth-child(1)::before {
    background-color: #d70206;
  }

  .ct-legend li:nth-child(2)::before {
    background-color: #f05b4f;
  }

  .ct-legend li:nth-child(3)::before {
    background-color: #f4c63d;
  }

  .ct-legend li:nth-child(1n+4)::before {
    background-color: #F06292;
  }

  .ct-legend .ct-legend-inside {
    position: absolute;
    top: 0;
    right: 0;
  }

  g:not(.ct-grids):not(.ct-labels) g:nth-child(1) .ct-point,
  g:not(.ct-grids):not(.ct-labels) g:nth-child(1) .ct-line {
    stroke: #d70206;
  }

  g:not(.ct-grids):not(.ct-labels) g:nth-child(2) .ct-point,
  g:not(.ct-grids):not(.ct-labels) g:nth-child(2) .ct-line {
    stroke: #f05b4f;
  }

  g:not(.ct-grids):not(.ct-labels) g:nth-child(3) .ct-point,
  g:not(.ct-grids):not(.ct-labels) g:nth-child(3) .ct-line {
    stroke: #f4c63d;
  }

  g:not(.ct-grids):not(.ct-labels) g:nth-child(1n+4) .ct-point,
  g:not(.ct-grids):not(.ct-labels) g:nth-child(1n+4) .ct-line {
    stroke: #F06292;
  }
</style>



// WEBPACK FOOTER //
// src/components/MPayBusinessDays.vue